import {
  CAMPAIGN_LABEL,
  DISCOUNT_LABEL,
  INSURANCE_LOADING_ROW_ID,
  YOU_HAVE_SAVED_LABEL,
  type SummaryItem,
} from '~/models/order/SummaryItem.model';

/**
 * Generates a price label for a subscription item based on the monthly price.
 */
export const makeMainPriceStringForSubscriptionItem = (
  monthlyPrice?: number,
  regularMonthlyPrice?: number,
  validityInMonths?: number,
) => {
  let priceString = '';
  if (validityInMonths && regularMonthlyPrice) {
    priceString = `${monthlyPrice?.toLocaleString('sv-SE')} kr/mån i ${validityInMonths} mån därefter ${regularMonthlyPrice?.toLocaleString('sv-SE')} kr/mån`;
  } else {
    priceString = monthlyPrice?.toLocaleString('sv-SE')
      ? `${monthlyPrice?.toLocaleString('sv-SE')} kr/mån`
      : 'Inget pris';
  }

  return priceString;
};

/**
 * Generates a price label for a prepaid item based on the monthly price.
 */
export const makeMainPriceStringForPrePaidItem = (OneTimePrice?: number) =>
  OneTimePrice ? `${OneTimePrice?.toLocaleString('sv-SE')} kr` : 'Inget pris';

/**
 * Constructs a binding period label for a subscription item based on the binding period.
 */
export const makeBindingPeriodStringForSubscriptionItem = (
  bindingPeriod?: number,
) => (bindingPeriod === 0 ? 'utan bindningstid' : `${bindingPeriod} mån`);

/**
 * Sort the `SummaryItem`s so that discount and campaign items are split, and items
 * with title 'Rabatt' come before the 'Du har sparat' row, but not items with title
 * 'Kampanj'
 */
export const sortDiscountsBeforeTheSavingsRow = (
  items: SummaryItem[],
): SummaryItem[] => {
  const discountItems = items.filter((item) => item.title === DISCOUNT_LABEL);
  const otherItems = items.filter((item) => item.title !== DISCOUNT_LABEL);

  const savingsIndex = otherItems.findIndex(
    (item) => item.title === YOU_HAVE_SAVED_LABEL,
  );

  if (savingsIndex === -1) {
    return items;
  }

  return [
    ...otherItems.slice(0, savingsIndex),
    ...discountItems,
    ...otherItems.slice(savingsIndex),
  ];
};

/**
 * If the items contain the loading row for insurance item that was just added,
 * sort the items so that loading row comes after products but before items with
 * main label 'Rabatt', 'Kampanj' and 'Du har sparat'
 */
export const sortInsuranceLoadingItemRowToWhereInsuranceRowIs = (
  items: SummaryItem[],
): SummaryItem[] => {
  const loadingRowIndex = items.findIndex(
    (item) => item.id === INSURANCE_LOADING_ROW_ID,
  );

  if (loadingRowIndex === -1) {
    return items;
  }

  const loadingRow = items[loadingRowIndex];
  const withoutLoadingRow = items.filter(
    (item) => item.id !== INSURANCE_LOADING_ROW_ID,
  );

  const discountIndex = withoutLoadingRow.findIndex(
    (item) => item.title === DISCOUNT_LABEL,
  );
  const campaignIndex = withoutLoadingRow.findIndex(
    (item) => item.title === CAMPAIGN_LABEL,
  );
  const savingsIndex = withoutLoadingRow.findIndex(
    (item) => item.title === YOU_HAVE_SAVED_LABEL,
  );

  const relevantIndices = [discountIndex, campaignIndex, savingsIndex].filter(
    (index) => index !== -1,
  );

  const insertIndex =
    relevantIndices.length > 0
      ? Math.min(...relevantIndices)
      : withoutLoadingRow.length;

  return [
    ...withoutLoadingRow.slice(0, insertIndex),
    loadingRow,
    ...withoutLoadingRow.slice(insertIndex),
  ];
};
